import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { Col, Spinner, Card, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import { LoadingState } from '../../../utilities/constants';
import { useDispatch, useSelector } from 'react-redux';
import { HorizontalBar } from 'react-chartjs-2';
import Empty from '../../../components/Empty';
import { fetchCarbonFootprintByScopeCategoryDashboard } from '../dashboardSlice';
import DashboardDownloadButton from '../components/DashboardDownloadButton';
import {
  formatNumber,
  createInitChartConfig,
  getColorForKey,
  parseScopeNumber,
  orderByWeightThenLabel,
} from '../../reports/reportHelper';
import { useTranslation } from 'react-i18next';
import { parseUserInfo } from '../../../utilities/common';

const DashboardGHGSnapshot = ({ filters, reportRangePeriod }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { orgId, usrDateFormat, usrTimezone } = parseUserInfo(useSelector((state) => state.users.user));
  const loggedUserStatus = useSelector((state) => state.users.loggedUserStatus);
  const ghgSnapshot = useSelector((state) => state.dashboard.carbonFootprintByScopeCategoryDashboard);
  const ghgSnapshotStatus = useSelector((state) => state.dashboard.carbonFootprintByScopeCategoryStatusDashboard);
  const ghgSnapshotError = useSelector((state) => state.dashboard.cfbscError);
  const [viewMode, setViewMode] = useState('absolute');
  const dataRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    const data = {
      usrTimezone,
      usrDateFormat,
      startDate: reportRangePeriod.startDate,
      endDate: reportRangePeriod.endDate,
      entitiesIdsToInclude: filters.selectedEntity,
      epsIdsToInclude: [],
      recentTime: null,
      scopeList: [],
      isApplyExcludedEPSs: true,
    };
    if (orgId && loggedUserStatus === LoadingState.succeeded) {
      try {
        dispatch(fetchCarbonFootprintByScopeCategoryDashboard({ orgId, data }));
      } catch (e) {
        toast.error(e.message);
      }
    }
  }, [
    filters.selectedEntity,
    orgId,
    dispatch,
    loggedUserStatus,
    reportRangePeriod.startDate,
    reportRangePeriod.endDate,
    usrTimezone,
    usrDateFormat,
  ]);

  const { labels, datasets, categories, totalOrganisationEmissions } = useMemo(() => {
    // Only Level 1 categories with emissions > 0 are eligible for the chart/legend.
    const scopeRows = _.orderBy(
      (ghgSnapshot || [])
        .map((item) => ({
          scopeNumber: parseScopeNumber(item.scope),
          records: (item.records || []).filter((record) => Number(record.co2_e_t) > 0),
        }))
        .filter((item) => item.scopeNumber !== null && item.records.length > 0)
        .map((item) => ({ ...item, scopeName: `Scope ${item.scopeNumber}` })),
      'scopeNumber',
      'asc'
    );

    // Categories follow the org's configured weight when set (matching the tree-ordering
    // convention in src/utilities/treeHelpers.js), falling back to alphabetical otherwise.
    const categoryMeta = _.uniqBy(
      scopeRows.flatMap((row) => row.records.map((record) => ({ label: record.category, weight: record.category_weight }))),
      'label'
    );
    const categoryNames = orderByWeightThenLabel(categoryMeta).map((item) => item.label);

    const chartDatasets = categoryNames.map((categoryName) => {
      const absoluteData = [];
      const percentData = [];
      scopeRows.forEach((row) => {
        const record = row.records.find((r) => r.category === categoryName);
        absoluteData.push(record ? Number(record.co2_e_t) : 0);
        percentData.push(record ? Number(record.per_co2_e_t) : 0);
      });
      return {
        label: categoryName,
        backgroundColor: getColorForKey(categoryName, 'scope'),
        data: viewMode === 'absolute' ? absoluteData : percentData,
        absoluteData,
      };
    });

    const total = _.sumBy(
      scopeRows.flatMap((row) => row.records),
      (record) => Number(record.co2_e_t)
    );

    return {
      labels: scopeRows.map((row) => row.scopeName),
      datasets: chartDatasets,
      categories: categoryNames,
      totalOrganisationEmissions: total,
    };
  }, [ghgSnapshot, viewMode]);

  const chartData = useMemo(() => ({ labels, datasets }), [labels, datasets]);

  const options = useMemo(
    () =>
      createInitChartConfig({
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              stacked: true,
              ticks: { beginAtZero: true },
              scaleLabel: {
                display: true,
                labelString: viewMode === 'absolute' ? t('CO2-e (t)') : t('Contribution (%)'),
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              gridLines: { display: false },
            },
          ],
        },
        tooltips: {
          filter: (tooltipItem, data) => data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] !== 0,
          callbacks: {
            title: (tooltipItems, data) => data.labels[tooltipItems[0].index],
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const value = dataset.absoluteData[tooltipItem.index];
              return t('{{category}}: {{value}} tCO2e', { category: dataset.label, value: formatNumber(value) });
            },
          },
        },
      }),
    [t, viewMode]
  );

  const isLoading = ghgSnapshotStatus === LoadingState.loading;
  const isReady = ghgSnapshotStatus === LoadingState.succeeded;
  const hasData = isReady && labels.length > 0;

  return (
    <Col lg="6">
      <Card className="h-100">
        <Card.Body>
          <div className="mt-2 mb-4 chart_print_wrapper" id="ghg_snapshot_chart" ref={dataRef}>
            <div className="dashboard-report-header">
              <strong className="report-text">{t('GHG Snapshot')}</strong>
              <div className="button-wrapper no-export" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ToggleButtonGroup type="radio" name="ghg-snapshot-view" value={viewMode} onChange={setViewMode}>
                  <ToggleButton
                    variant="outline-secondary"
                    value="absolute"
                    id="ghg-snapshot-absolute"
                    style={{ padding: '5px 14px', fontSize: '13px' }}
                  >
                    {t('Absolute tCO2e')}
                  </ToggleButton>
                  <ToggleButton
                    variant="outline-secondary"
                    value="proportional"
                    id="ghg-snapshot-proportional"
                    style={{ padding: '5px 14px', fontSize: '13px' }}
                  >
                    {t('Proportional Contribution (%)')}
                  </ToggleButton>
                </ToggleButtonGroup>
                <DashboardDownloadButton downloadRef={dataRef} reportName={t('GHG Snapshot')} disabled={!hasData} />
              </div>
            </div>
            {isLoading && <Spinner className="loader" animation="grow" />}
            {hasData && (
              <>
                <div style={{ display: 'flex', width: '100%', gap: '20px' }}>
                  <div style={{ flex: 1, height: '300px', minWidth: 0 }}>
                    <HorizontalBar data={chartData} options={options} ref={chartRef} />
                  </div>
                  <ul className="chart-legend" style={{ width: '200px', flexShrink: 0, height: 'auto', paddingLeft: '0px' }}>
                    {categories.map((category) => (
                      <li key={category} style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                        <span style={{ backgroundColor: getColorForKey(category, 'scope') }} />
                        {category}
                      </li>
                    ))}
                  </ul>
                </div>
                {viewMode === 'absolute' && (
                  <p className="mt-3">
                    {t('Total Organisation Emissions: {{value}} tCO2e', { value: formatNumber(totalOrganisationEmissions) })}
                  </p>
                )}
              </>
            )}
            {isReady && !hasData && <Empty title={t('No emissions data available for the selected filters.')} />}
            {ghgSnapshotStatus === LoadingState.failed && ghgSnapshotError && <Empty title={ghgSnapshotError} />}
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default DashboardGHGSnapshot;
